export interface PortfolioProject {
  slug: string;
  title: string;
  category: string;
  filter: string;
  kind: string;
  summary: string;
  description: string[];
  skills: string[];
  images: { src: string; alt: string }[];
}

export const projects: PortfolioProject[] = [
  {
    "slug": "nexora",
    "title": "Nexora",
    "category": "Web Design & Development",
    "filter": "Websites",
    "kind": "Project",
    "summary": "A responsive React website showcasing technology services through intuitive navigation and clear service sections.",
    "description": [
      "Designed and developed a responsive React website that presents Nexora’s technology services through a clear, professional interface. Structured service sections, intuitive navigation, and prominent calls to action help visitors explore solutions and connect with the team.",
      "Built with reusable components and layouts adapted for desktop, tablet, and mobile, the website combines a consistent visual identity with a maintainable frontend."
    ],
    "skills": [
      "React",
      "UI Design",
      "Responsive Development"
    ],
    "images": [
      {
        "src": "/portfolio/nexora-1.png",
        "alt": "Technology solutions website across desktop, tablet, and mobile"
      }
    ]
  },
  {
    "slug": "ai-automation",
    "title": "AI Automation Solutions",
    "category": "AI & Workflow Automation",
    "filter": "AI & Automation",
    "kind": "Collection",
    "summary": "AI chatbots, connected business tools, and automated workflows for lead management, customer support, and content creation.",
    "description": [
      "Custom AI automation solutions that connect business tools and simplify everyday workflows. The work includes AI chatbots, OpenAI API integrations, lead qualification, customer support automation, and content workflows.",
      "Designed around practical business needs, these systems help teams manage enquiries, organise leads, and automate repetitive tasks through clear interfaces and connected applications."
    ],
    "skills": [
      "AI Automation",
      "API Integration",
      "Chatbots",
      "Workflow Design"
    ],
    "images": [
      {
        "src": "/portfolio/ai-automation-1.png",
        "alt": "AI automation collection"
      },
      {
        "src": "/portfolio/ai-automation-2.png",
        "alt": "Lead management dashboard"
      },
      {
        "src": "/portfolio/ai-automation-3.png",
        "alt": "Accreditation management dashboard"
      },
      {
        "src": "/portfolio/ai-automation-4.png",
        "alt": "Marketing workflow dashboard"
      },
      {
        "src": "/portfolio/ai-automation-5.png",
        "alt": "AI writing workspace"
      }
    ]
  },
  {
    "slug": "crm-erp",
    "title": "CRM & ERP Solutions",
    "category": "Business Management Software",
    "filter": "Business Platforms",
    "kind": "Collection",
    "summary": "Connected platforms for managing sales, teams, inventory, and reporting through clear dashboards and automated workflows.",
    "description": [
      "Custom CRM and ERP platforms that bring sales, employee management, inventory, and reporting into one connected system. Each solution is tailored to the organisation’s workflows, helping teams organise information, track progress, and manage daily operations.",
      "Clear dashboards, intuitive navigation, and responsive interfaces make complex tasks easier to manage. Workflow automation reduces repetitive administration, while reporting gives teams better visibility into business activity."
    ],
    "skills": [
      "CRM & ERP",
      "Dashboard Design",
      "Workflow Automation",
      "Reporting"
    ],
    "images": [
      {
        "src": "/portfolio/crm-erp-1.png",
        "alt": "CRM and ERP collection"
      },
      {
        "src": "/portfolio/crm-erp-2.png",
        "alt": "Sales and customer dashboard"
      },
      {
        "src": "/portfolio/crm-erp-3.png",
        "alt": "Staff and operations dashboard"
      },
      {
        "src": "/portfolio/crm-erp-4.png",
        "alt": "Project and task management"
      },
      {
        "src": "/portfolio/crm-erp-5.png",
        "alt": "Team workload overview"
      },
      {
        "src": "/portfolio/crm-erp-6.png",
        "alt": "Marketing and customer management"
      }
    ]
  },
  {
    "slug": "saas",
    "title": "SaaS Platforms",
    "category": "SaaS & Web Applications",
    "filter": "Business Platforms",
    "kind": "Collection",
    "summary": "Custom SaaS platforms, dashboards, and membership portals that simplify daily work through intuitive interfaces.",
    "description": [
      "Custom SaaS platforms built around the needs of businesses and their users. The work includes admin dashboards, CRM systems, membership platforms, and business portals, with responsive interfaces for desktop, tablet, and mobile.",
      "Each platform combines clear navigation, practical automation, and organised workflows to make everyday tasks easier. The focus is on helping users find information, manage activity, and complete their work through a consistent interface."
    ],
    "skills": [
      "SaaS Development",
      "Admin Dashboards",
      "Membership Portals",
      "Responsive UI"
    ],
    "images": [
      {
        "src": "/portfolio/saas-1.png",
        "alt": "SaaS development collection"
      },
      {
        "src": "/portfolio/saas-2.png",
        "alt": "Employee management dashboard"
      },
      {
        "src": "/portfolio/saas-3.png",
        "alt": "Email automation platform"
      },
      {
        "src": "/portfolio/saas-4.png",
        "alt": "Appointment management interface"
      },
      {
        "src": "/portfolio/saas-5.png",
        "alt": "Community and membership platform"
      },
      {
        "src": "/portfolio/saas-6.png",
        "alt": "AI voice agent website"
      }
    ]
  },
  {
    "slug": "leadpilot",
    "title": "LeadPilot",
    "category": "AI Lead Generation & Outreach",
    "filter": "AI & Automation",
    "kind": "Project",
    "summary": "An AI-assisted lead generation workflow combining prospect research, qualification, personalised outreach, and campaign tracking.",
    "description": [
      "Developed and executed an AI-assisted workflow for finding relevant prospects, qualifying leads, and managing personalised outreach. The project combined targeted research, organised contact lists, email campaigns, and follow-up workflows into a structured process.",
      "Campaign activity and response data were analysed to identify opportunities to refine targeting and messaging. The workflow was designed to reduce manual research and help teams manage prospects from initial discovery through ongoing engagement."
    ],
    "skills": [
      "Data Mining",
      "Email Marketing",
      "Data Analysis",
      "AI Automation",
      "Lead Qualification"
    ],
    "images": [
      {
        "src": "/portfolio/leadpilot-1.png",
        "alt": "LeadPilot overview across desktop and mobile"
      },
      {
        "src": "/portfolio/leadpilot-2.png",
        "alt": "Lead generation tasks and tools"
      },
      {
        "src": "/portfolio/leadpilot-3.png",
        "alt": "Research, outreach, and lead management workflow"
      }
    ]
  },
  {
    "slug": "consistly",
    "title": "Consistly.ai",
    "category": "AI SaaS & Content Automation",
    "filter": "AI & Automation",
    "kind": "Project",
    "summary": "The AI and automation engine connecting personalised content strategy, weekly ideas, voice-matched drafts, and LinkedIn publishing.",
    "description": [
      "Built the AI and automation layer for Consistly.ai, a commercial platform that helps users plan, write, and publish LinkedIn content in their own voice.",
      "The system captures each user’s expertise, audience, business goals, and writing samples during onboarding. This stored profile guides content strategy, weekly ideas, and personalised drafts.",
      "A scheduled workflow generates ten to twelve ideas each week, including hooks, talking points, recommended formats, and publishing days. Drafts draw on the user’s existing posts to reflect their writing style, while automated scheduling and publishing support a consistent posting routine."
    ],
    "skills": [
      "AI Engineering",
      "Workflow Automation",
      "LinkedIn Integration",
      "Content Strategy"
    ],
    "images": [
      {
        "src": "/portfolio/consistly-1.png",
        "alt": "Consistly content platform overview"
      },
      {
        "src": "/portfolio/consistly-2.png",
        "alt": "Content planning, drafting, and publishing engine"
      }
    ]
  },
  {
    "slug": "pronuvia",
    "title": "Pronuvia",
    "category": "E-commerce & WordPress Development",
    "filter": "Websites",
    "kind": "Project",
    "summary": "A WordPress and WooCommerce store for wellness products, with clear categories, responsive layouts, and a guided shopping experience.",
    "description": [
      "Designed and developed a health and wellness online store using WordPress and WooCommerce. The website combines a calm visual identity with clear product categories, intuitive navigation, and layouts that help visitors explore products and move toward purchase.",
      "The work included custom homepage and product sections, newsletter signup, mobile layouts, and navigation and footer design. Responsive and cross-browser testing supported a consistent shopping experience across devices."
    ],
    "skills": [
      "WordPress",
      "WooCommerce",
      "UI Design",
      "Responsive Development",
      "Database Management"
    ],
    "images": [
      {
        "src": "/portfolio/pronuvia-1.png",
        "alt": "Pronuvia storefront and newsletter signup"
      },
      {
        "src": "/portfolio/pronuvia-2.png",
        "alt": "Product categories and homepage"
      },
      {
        "src": "/portfolio/pronuvia-3.png",
        "alt": "Newsletter and footer design"
      }
    ]
  }
];
export const portfolioFilters = ['All work', 'Websites', 'AI & Automation', 'Business Platforms'] as const;

